import {useCallback, useEffect, useMemo, useState} from "react";
import {
    createUser,
    definirAcessoAoPortal,
    deleteUser,
    listAllUsers,
    listLicencasReference,
    listRolesReference,
    updateUser
} from "./UsersService";
import {
    acessoParaPayload,
    buildUserStats,
    extractListPayload,
    getBackendMessage,
    normalizeUserRows
} from "./usersUtils";
import {portalDaConta, portalParaPayload, validarPortal} from "./portalUtils";
import {useDebounce} from "../../shared/hooks";

const createEmptyForm = () => ({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    ddiPhoneNumber: '55',
    mobilePhoneNumber: '',
    enabled: true,
    blocked: false,
    mustRenewPassword: false,
    expirationDate: '',
    specialAccessType: 'USER',
    roles: [],
    licenses: [],
    vinculos: [],
});

const normalizeToForm = (row) => ({
    ...createEmptyForm(),
    ...row,
    password: '',
    confirmPassword: '',
    expirationDate: row?.expirationDate ? String(row.expirationDate).slice(0, 10) : '',
    roles: Array.isArray(row?.roles) ? row.roles : [],
    licenses: Array.isArray(row?.licenses) ? row.licenses : [],
    vinculos: Array.isArray(row?.vinculos) ? row.vinculos : [],
});

const buildPayload = (form) => {
    const expirationDate = form.expirationDate || null;

    return {
        userName: form.username?.trim(),
        email: form.email?.trim(),
        password: form.password?.trim() || undefined,
        ddi: form.ddiPhoneNumber?.trim() || null,
        phoneNumber: form.mobilePhoneNumber?.trim() || null,
        accessType: form.specialAccessType,
        userNotExpires: !expirationDate,
        expirationDate,
        active: Boolean(form.enabled),
        blocked: Boolean(form.blocked),
        mustRenewPassword: Boolean(form.mustRenewPassword),
        ...acessoParaPayload(form),
    };
};

/** @param enabled sem permissão para a tela, nada é carregado. */
export const useUsers = ({enabled = true} = {}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({error: '', success: ''});
    const [filter, setFilter] = useState('');
    const [accessTypeFilter, setAccessTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [blockedFilter, setBlockedFilter] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savingPortal, setSavingPortal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [form, setForm] = useState(createEmptyForm());
    const [references, setReferences] = useState({roles: [], licencas: []});
    const debouncedFilter = useDebounce(filter, 400);

    const loadReferences = useCallback(async () => {
        try {
            const [rolesResponse, licencasResponse] = await Promise.all([
                listRolesReference(),
                listLicencasReference(),
            ]);

            setReferences({
                roles: extractListPayload(rolesResponse).items,
                licencas: extractListPayload(licencasResponse).items,
            });
        } catch {
            // silencioso
        }
    }, []);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const normalizedFilter = debouncedFilter.trim();
            const isEmailSearch = normalizedFilter.includes('@');
            const response = await listAllUsers({
                username: normalizedFilter && !isEmailSearch ? normalizedFilter : '',
                email: normalizedFilter && isEmailSearch ? normalizedFilter : '',
                accessType: accessTypeFilter,
                active: statusFilter === 'ATIVO' ? true : statusFilter === 'INATIVO' ? false : '',
                blocked: blockedFilter === 'SIM' ? true : blockedFilter === 'NAO' ? false : statusFilter === 'BLOQUEADO' ? true : '',
            });
            setRows(normalizeUserRows(extractListPayload(response).items));
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível carregar os usuários.'), success: ''});
        } finally {
            setLoading(false);
        }
    }, [accessTypeFilter, blockedFilter, debouncedFilter, statusFilter]);

    useEffect(() => {
        if (enabled) loadReferences();
    }, [enabled, loadReferences]);

    useEffect(() => {
        if (enabled) loadUsers();
    }, [enabled, loadUsers]);

    const stats = useMemo(() => buildUserStats(rows), [rows]);

    const openCreate = useCallback(() => {
        setEditing(null);
        setForm(createEmptyForm());
        setDialogOpen(true);
    }, []);

    const openEdit = useCallback((row) => {
        setEditing(row);
        setForm(normalizeToForm(row));
        setDialogOpen(true);
    }, []);

    const submit = useCallback(async () => {
        if (!form.username?.trim() || !form.email?.trim()) {
            setFeedback({error: 'Usuário e e-mail são obrigatórios.', success: ''});
            return;
        }

        if (!(editing?.userId || editing?.id)) {
            if (!form.password?.trim()) {
                setFeedback({error: 'Senha é obrigatória para criar um usuário.', success: ''});
                return;
            }
            if (form.password !== form.confirmPassword) {
                setFeedback({error: 'Senha e confirmação de senha devem ser iguais.', success: ''});
                return;
            }
        }

        setSaving(true);
        try {
            const payload = buildPayload(form);
            if (editing?.userId || editing?.id) {
                await updateUser(editing.userId || editing.id, {
                    userId: editing.userId || editing.id,
                    username: form.username?.trim(),
                    email: form.email?.trim(),
                    ddi: form.ddiPhoneNumber?.trim() || null,
                    phoneNumber: form.mobilePhoneNumber?.trim() || null,
                    accessType: form.specialAccessType,
                    expirationDate: form.expirationDate || null,
                    blocked: Boolean(form.blocked),
                    active: Boolean(form.enabled),
                    mustRenewPassword: Boolean(form.mustRenewPassword),
                    ...acessoParaPayload(form, editing),
                });
                setFeedback({error: '', success: 'Usuário atualizado com sucesso.'});
            } else {
                await createUser(payload);
                setFeedback({error: '', success: 'Usuário criado com sucesso.'});
            }
            setDialogOpen(false);
            await loadUsers();
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível salvar o usuário.'), success: ''});
        } finally {
            setSaving(false);
        }
    }, [editing, form, loadUsers]);

    /**
     * Acesso ao portal do cliente e visões da conta (BBF-179), pela rota própria do identity e com
     * botão próprio: "Salvar alterações" (o PUT do usuário) não leva o portal. A resposta é a conta
     * atualizada, e a lista e a conta em edição passam a mostrar o que ficou gravado sem recarregar
     * a lista inteira nem mexer no formulário ainda não salvo.
     *
     * @param portal `{acesso, visoes}` da tela
     * @return `true` quando gravou
     */
    const salvarPortal = useCallback(async (conta, portal) => {
        const userId = conta?.userId || conta?.id;
        if (!userId) return false;

        const erro = validarPortal(portal);
        if (erro) {
            setFeedback({error: erro, success: ''});
            return false;
        }

        setSavingPortal(true);
        try {
            const corpo = portalParaPayload(portal);
            const response = await definirAcessoAoPortal(userId, corpo);
            const lida = response?.data?.valor || response?.data;
            const gravado = typeof lida?.acessoPortal === 'boolean' ? portalDaConta(lida) : corpo;
            const mudanca = {acessoPortal: gravado.acesso, visoesPortal: gravado.visoes};
            const daConta = (item) => (item?.userId || item?.id) === userId;

            setRows((current) => current.map((row) => (daConta(row) ? {...row, ...mudanca} : row)));
            setEditing((current) => (daConta(current) ? {...current, ...mudanca} : current));
            setFeedback({error: '', success: 'Acesso ao portal atualizado.'});
            return true;
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível salvar o acesso ao portal.'), success: ''});
            return false;
        } finally {
            setSavingPortal(false);
        }
    }, []);

    const updateStatus = useCallback(async (row, overrides) => {
        try {
            await updateUser(row.userId || row.id, {
                userId: row.userId || row.id,
                username: row.username,
                email: row.email,
                ddi: row.ddiPhoneNumber || null,
                phoneNumber: row.mobilePhoneNumber || null,
                accessType: row.specialAccessType,
                expirationDate: row.expirationDate || null,
                blocked: overrides?.blocked ?? row.blocked,
                active: overrides?.enabled ?? row.enabled,
                mustRenewPassword: overrides?.mustRenewPassword ?? row.mustRenewPassword,
                roleIdList: (row.roles || []).map((item) => item?.roleId || item?.id).filter(Boolean),
                licenseIdList: (row.licenses || []).map((item) => item?.id || item?.licenseId).filter(Boolean),
            });
            setFeedback({error: '', success: 'Status do usuário atualizado com sucesso.'});
            await loadUsers();
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível atualizar o status do usuário.'), success: ''});
        }
    }, [loadUsers]);

    const confirmDeleteTarget = useCallback(async () => {
        if (!(confirmDelete?.userId || confirmDelete?.id)) return;
        try {
            await deleteUser(confirmDelete.userId || confirmDelete.id);
            setConfirmDelete(null);
            setFeedback({error: '', success: 'Usuário excluído com sucesso.'});
            await loadUsers();
        } catch (error) {
            setFeedback({error: getBackendMessage(error, 'Não foi possível excluir o usuário.'), success: ''});
        }
    }, [confirmDelete, loadUsers]);

    return {
        rows,
        loading,
        feedback,
        setFeedback,
        filter,
        setFilter,
        accessTypeFilter,
        setAccessTypeFilter,
        statusFilter,
        setStatusFilter,
        blockedFilter,
        setBlockedFilter,
        dialogOpen,
        setDialogOpen,
        editing,
        saving,
        savingPortal,
        form,
        setForm,
        references,
        stats,
        confirmDelete,
        setConfirmDelete,
        openCreate,
        openEdit,
        submit,
        salvarPortal,
        updateStatus,
        confirmDeleteTarget,
    };
};
